import { useEffect, useState } from "react";
import api, { fmtMoney } from "../../../api";
import { useSettings } from "./useSettings";

const PROVINCES = { ON: "Ontario", NS: "Nova Scotia", NB: "New Brunswick", NL: "Newfoundland and Labrador", PE: "Prince Edward Island", AB: "Alberta", YT: "Yukon", NT: "Northwest Territories", NU: "Nunavut", BC: "British Columbia", SK: "Saskatchewan", MB: "Manitoba", QC: "Quebec" };

export default function SettingsTax() {
  const { s, setS, save } = useSettings();
  const [metrics, setMetrics] = useState(null);
  useEffect(() => { api.get("/metrics").then((r) => setMetrics(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  const taxUnsupported = (s.unsupported_tax_jurisdictions || []).includes(s.tax_jurisdiction);

  return (
    <div className="space-y-8">
      <section id="set-tax" className="card-flat space-y-3 p-6" data-testid="tax-settings">
        <p className="label-caps">{s.tax_label || "HST"} ({PROVINCES[s.tax_jurisdiction] || s.tax_jurisdiction || "Ontario"})</p>
        <p className="text-sm text-muted-foreground">
          You don't need to charge {s.tax_label || "HST"} until your revenue passes $30,000 over four straight quarters. Turn this on once you register — every new invoice gets a {s.tax_label || "HST"} line automatically.
        </p>
        {metrics && (
          <div className="rounded-md bg-muted p-3" data-testid="tax-progress">
            <div className="flex justify-between text-xs font-bold">
              <span>Revenue, last 12 months: {fmtMoney(metrics.revenue_trailing_12m)}</span>
              <span>{metrics.tax_threshold_pct}% of $30,000</span>
            </div>
            <div className="mt-2 h-2 rounded-full bg-border">
              <div className={`h-2 rounded-full transition-[width] ${metrics.tax_threshold_pct >= 90 ? "bg-destructive" : "bg-primary"}`} style={{ width: `${metrics.tax_threshold_pct}%` }} />
            </div>
            {metrics.tax_threshold_pct >= 90 && <p className="mt-2 text-xs font-bold text-destructive">You're near the $30,000 small-supplier threshold — time to register for {s.tax_label || "HST"}.</p>}
          </div>
        )}
        <label className="block text-sm font-semibold">Province / territory
          <select data-testid="tax-jurisdiction" className="field-input mt-1 w-full" value={s.tax_jurisdiction || "ON"}
            onChange={(e) => {
              const jur = e.target.value;
              const preset = (s.tax_presets || {})[jur];
              setS({ ...s, tax_jurisdiction: jur, ...(preset ? { tax_label: preset.label, tax_rate: preset.rate } : {}) });
            }}>
            {Object.keys(PROVINCES).map((code) => <option key={code} value={code}>{PROVINCES[code]}</option>)}
          </select>
        </label>
        {taxUnsupported && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm" data-testid="tax-unsupported-msg">
            {PROVINCES[s.tax_jurisdiction] || s.tax_jurisdiction} charges GST plus a separate provincial tax, which needs two tax lines on an invoice. RPM Field doesn't support that yet — get in touch and we'll let you know when it lands.
          </div>
        )}
        <label className="flex items-center justify-between rounded-md border border-border p-4">
          <span className="text-sm font-semibold">Charge {s.tax_label || "HST"} on invoices</span>
          <button data-testid="tax-toggle" disabled={taxUnsupported && !s.tax_enabled} onClick={() => save({ tax_enabled: !s.tax_enabled })}
            className={`h-8 w-14 rounded-full transition-colors disabled:opacity-40 ${s.tax_enabled ? "bg-primary" : "bg-muted"}`}>
            <span className={`block h-6 w-6 translate-x-1 rounded-full bg-white transition-transform ${s.tax_enabled ? "translate-x-7" : ""}`} />
          </button>
        </label>
        {s.tax_enabled && !taxUnsupported && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input data-testid="tax-rate" type="number" className="field-input w-28" value={s.tax_rate} onFocus={(e) => e.target.select()}
                onChange={(e) => setS({ ...s, tax_rate: Number(e.target.value) })} />
              <span className="text-sm font-semibold">% rate</span>
            </div>
            <input data-testid="tax-label" className="field-input w-full" placeholder="Tax label (e.g. HST, GST)" value={s.tax_label || ""}
              onChange={(e) => setS({ ...s, tax_label: e.target.value })} />
            <input data-testid="tax-number" className="field-input w-full" placeholder={`${s.tax_label || "Tax"} registration number (shows on invoices)`} value={s.tax_number}
              onChange={(e) => setS({ ...s, tax_number: e.target.value })} />
            <button data-testid="tax-save" onClick={() => save({ tax_jurisdiction: s.tax_jurisdiction, tax_label: s.tax_label, tax_rate: Number(s.tax_rate), tax_number: s.tax_number })} className="h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Save tax details</button>
          </div>
        )}
      </section>
    </div>
  );
}
